import { FC, useMemo } from 'react'
import { FutureLayerUID, FutureLayer } from 'components/features/future/types'
import { FuturePanePreset } from 'components/features/future/FuturePanePreset/FuturePanePreset'
import * as yup from 'yup'
import { useLayerSelector, extendCommonSchema } from 'components/features/future/helpers'
import { useFormContext } from 'react-hook-form'
import { FutureWhenInput } from 'components/features/future/FutureWhenInput/FutureWhenInput'
import { maskPercentFromZero, maskCurrencyFromZero, maskNumberPositiveTo1200 } from 'components/atoms/inputs/maskedInput'
import { FutureFieldSlider } from 'components/features/future/FutureField/FutureFieldSlider'
import { schemaNumber, schemaNumberInString } from 'schemas/baseSchema'
import { FutureFieldButtons } from 'components/features/future/FutureField/FutureFieldButtons'
import { useCollectLayerErrors } from 'components/features/future/hooks/useCollectErrors'
import { ObjectSchema } from 'yup'
import { LayerStatus } from 'components/features/future/LayerStatus/LayerStatus'

const LAYER_SLUG = 'get_degree' as const
type LayerSlugType = typeof LAYER_SLUG
type LayerType = Extract<FutureLayer, { slug: LayerSlugType }>

const schemaCreator = extendCommonSchema<LayerSlugType>(yup.object({
  duration_months: schemaNumber,
  income_adjustment_during: schemaNumberInString,
  salary_after_graduation: schemaNumber,
  tuition_amount: schemaNumber,
}) as ObjectSchema<LayerType>)

type FutureLayerPaneGetDegreeProps = {
  uid?: FutureLayerUID
  onCancel: () => void
}

export const FutureLayerPaneGetDegree: FC<FutureLayerPaneGetDegreeProps> = (props) => {
  const { onCancel, uid } = props
  const layer = useLayerSelector<LayerSlugType>(props)

  return (
    <FuturePanePreset<LayerSlugType>
      layer={ layer }
      slug={ LAYER_SLUG }
      name='Get a Degree'
      icon='education-solid'
      type='goal'
      schemaCreator={ schemaCreator }
      onCancel={ onCancel }
    >
      <FormFields uid={ uid } />
    </FuturePanePreset>
  )
}

type FormFieldsProps = {
  uid?: FutureLayerUID
}
const FormFields: FC<FormFieldsProps> = (props) => {
  const { uid } = props
  const { watch } = useFormContext<LayerType>()

  const duration_months = watch('duration_months')
  const errors = useCollectLayerErrors<LayerType>()

  const completeChild = useMemo(() => (
    <>
      <h1>
        Goal Achieved. Congratulations!
      </h1>
      Remember to update your Settings and Plan:
      <ul>
        <li>Update your current income in Settings</li>
        <li>Add an Income Change event to your Plan to reflect your expected income after graduation</li>
      </ul>
    </>
  ), [])

  return (
    <LayerStatus uid={ uid || null } skipPartialState completeContent={ completeChild } type='goal'>
      <FutureWhenInput error={ errors.whenField } />

      <FutureFieldSlider
        label='Months of Study'
        name='duration_months'
        mask={ maskNumberPositiveTo1200 }

        error={ errors.duration_months }
        note={ !!duration_months && 'That would equal ' + (duration_months / 12).toFixed(2) + ' years' }

        min={ 1 }
        max={ 96 }
        step={ 1 }
      />

      <FutureFieldSlider
        label='Tuition Cost'
        name='tuition_amount'
        mask={ maskCurrencyFromZero }

        error={ errors.tuition_amount }
        units='per year'

        min={ 0 }
        max={ 200000 }
        step={ 500 }
      />

      <FutureFieldButtons
        label='Income reduction during school'
        name='income_adjustment_during'
        mask={ maskPercentFromZero }
        inputMode='decimal'

        error={ errors.income_adjustment_during }
        min={ 0 }
        max={ 30 }
        step={ 1 }
      />

      <FutureFieldSlider
        label='New salary after graduation'
        name='salary_after_graduation'
        mask={ maskCurrencyFromZero }

        error={ errors.salary_after_graduation }

        min={ 0 }
        max={ 300000 }
        step={ 5000 }
      />

    </LayerStatus>
  )
}
